import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import GridOnIcon from "@mui/icons-material/GridOn";

import Button from "src/components/Button";
import { captureHandledException } from "src/services/sentry";
import { exportExcel, type ExcelSheet } from "src/utils/excelExport";

interface ExcelExportButtonProps {
  // File name prefix, the same one the screenshot uses
  reportName: string;
  calculatedAt: string | null | undefined;
  // Builds row 1 and the table from what is on screen at the moment of the click
  buildExport: () => { contextLine: string; sheet: ExcelSheet };
}

const ExcelExportButton: FC<ExcelExportButtonProps> = ({ reportName, calculatedAt, buildExport }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar: snack } = useSnackbar();
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const { contextLine, sheet } = buildExport();
      await exportExcel({ reportName, calculatedAt, contextLine, sheet });
    } catch (err) {
      captureHandledException(err, { feature: reportName, component: "ExcelExportButton", action: "exportExcel" });
      snack(t("excel_export.failed"), { variant: "error" });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Button
      icon={<GridOnIcon />}
      onClick={handleExport}
      disabled={isExporting}
      iconSize={"large"}
      title={t("export_to_excel")}
    />
  );
};

export default ExcelExportButton;
